import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Phone, Mail, MapPin, Clock, Printer, Star, ExternalLink,
  Facebook, Linkedin, Shield, FileText, Scale, Accessibility, DollarSign
} from 'lucide-react';
import { siteConfig, clinician, googleReviews } from '../../data/content';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { path: '/', label: 'Home' },
    { path: '/about', label: 'About Gilda' },
    { path: '/services', label: 'Services' },
    { path: '/testimonials', label: 'Testimonials' },
    { path: '/contact', label: 'Contact' },
  ];

  const patientLinks = [
    { path: '/start-here', label: 'Start Here (New Patients)' },
    { path: '/resources', label: 'Patient Education' },
    { path: '/insurance-info', label: 'Insurance & Billing' },
    { path: '/provider-referrals', label: 'Provider Referrals' },
  ];

  const legalLinks = [
    { path: '/privacy', label: 'Privacy Policy', icon: Shield },
    { path: '/notice-of-privacy-practices', label: 'Notice of Privacy Practices', icon: FileText },
    { path: '/terms', label: 'Terms of Use', icon: Scale },
    { path: '/accessibility', label: 'Accessibility', icon: Accessibility },
    { path: '/good-faith-estimate', label: 'Good Faith Estimate', icon: DollarSign },
  ];

  return (
    <footer className="bg-slate-900 text-slate-300" role="contentinfo">
      {/* Main Footer */}
      <div className="container py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          
          {/* Brand & Contact */}
          <div className="lg:col-span-1">
            <Link to="/" className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-teal-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-lg">D</span>
              </div>
              <div>
                <span className="font-semibold text-white text-lg leading-tight block">
                  Dysphagia Therapy
                </span>
                <span className="text-teal-400 text-xs font-medium tracking-wide">
                  PLUS CM, LLC
                </span>
              </div>
            </Link>
            <p className="text-sm text-slate-400 mb-6 leading-relaxed">
              Specialized speech-language pathology services for swallowing disorders 
              and communication needs in the Greater Houston area.
            </p>
            
            <div className="space-y-3">
              <a 
                href={`tel:${siteConfig.phone}`}
                className="flex items-center gap-3 text-sm hover:text-teal-400 transition-colors"
                data-testid="footer-phone"
              >
                <Phone size={16} className="text-teal-500" />
                <span><span className="text-slate-500 text-xs mr-1">Office:</span> {siteConfig.phone}</span>
              </a>
              {siteConfig.fax && siteConfig.faxVerified && (
                <a 
                  href={`tel:${siteConfig.fax}`}
                  className="flex items-center gap-3 text-sm hover:text-teal-400 transition-colors"
                  data-testid="footer-fax"
                >
                  <Printer size={16} className="text-teal-500" />
                  <span><span className="text-slate-500 text-xs mr-1">Secure Fax:</span> {siteConfig.fax}</span>
                </a>
              )}
              <a 
                href={`mailto:${siteConfig.email}`}
                className="flex items-center gap-3 text-sm hover:text-teal-400 transition-colors"
                data-testid="footer-email"
              >
                <Mail size={16} className="text-teal-500" />
                {siteConfig.email}
              </a>
              <div className="flex items-start gap-3 text-sm">
                <MapPin size={16} className="text-teal-500 flex-shrink-0 mt-0.5" />
                <span>Greater Houston, TX Area<br />Mobile In-Home Services</span>
              </div>
            </div>
            
            {/* Google Reviews CTA */}
            {googleReviews.enabled && googleReviews.reviewsUrl && (
              <div className="mt-6 pt-6 border-t border-slate-700">
                <a 
                  href={googleReviews.reviewsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-teal-600 hover:bg-teal-500 text-white text-sm font-medium rounded-lg transition-colors"
                  data-testid="footer-google-review-cta"
                >
                  <Star size={16} className="fill-current" />
                  {googleReviews.ctaLeave}
                  <ExternalLink size={14} />
                </a>
                <p className="text-xs text-slate-500 mt-2">
                  {googleReviews.businessAddress}
                </p>
              </div>
            )}
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-white font-semibold mb-4">Quick Links</h3>
            <ul className="space-y-2">
              {quickLinks.map((link, i) => (
                <li key={i}>
                  <Link 
                    to={link.path}
                    className="text-sm hover:text-teal-400 transition-colors"
                    data-testid={`footer-link-${link.path.replace('/', '') || 'home'}`}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Patient Resources */}
          <div>
            <h3 className="text-white font-semibold mb-4">For Patients</h3>
            <ul className="space-y-2">
              {patientLinks.map((link, i) => (
                <li key={i}>
                  <Link 
                    to={link.path}
                    className="text-sm hover:text-teal-400 transition-colors"
                    data-testid={`footer-link-${link.path.replace('/', '')}`}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
            
            {/* Office Hours */}
            <div className="mt-6">
              <h4 className="text-white font-medium text-sm mb-2 flex items-center gap-2">
                <Clock size={14} className="text-teal-500" />
                Office Hours
              </h4>
              <p className="text-xs text-slate-400">
                {siteConfig.hours}<br />
                {siteConfig.hoursNote}
              </p>
            </div>
          </div>

          {/* Legal & Compliance */}
          <div>
            <h3 className="text-white font-semibold mb-4">Legal & Privacy</h3>
            <ul className="space-y-2">
              {legalLinks.map((link, i) => (
                <li key={i}>
                  <Link 
                    to={link.path}
                    className="flex items-center gap-2 text-sm hover:text-teal-400 transition-colors"
                    data-testid={`footer-link-${link.path.replace('/', '')}`}
                  >
                    <link.icon size={14} className="text-slate-500" />
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>

            {/* Social Links */}
            <div className="mt-6">
              <h4 className="text-white font-medium text-sm mb-3">Connect</h4>
              <div className="flex items-center gap-3">
                <a 
                  href="#" 
                  className="w-9 h-9 bg-slate-800 rounded-lg flex items-center justify-center hover:bg-teal-600 transition-colors"
                  aria-label="Facebook"
                  data-testid="footer-facebook"
                >
                  <Facebook size={18} />
                </a>
                <a 
                  href="#" 
                  className="w-9 h-9 bg-slate-800 rounded-lg flex items-center justify-center hover:bg-teal-600 transition-colors"
                  aria-label="LinkedIn"
                  data-testid="footer-linkedin"
                >
                  <Linkedin size={18} />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-slate-800">
        <div className="container py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-slate-500">
            <p>
              © {currentYear} {siteConfig.name}. All rights reserved.
            </p>
            <p className="text-center md:text-right">
              Serving Greater Houston, Harris County, Fort Bend County & Montgomery County
            </p>
          </div>
          
          {/* Disclaimer */}
          <p className="text-xs text-slate-600 mt-4 text-center max-w-3xl mx-auto">
            The information on this website is for general informational purposes only and 
            is not intended as medical advice. Please consult a qualified healthcare provider 
            for diagnosis and treatment. Do not submit protected health information (PHI) through 
            website contact forms.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
